import { useRouter } from 'next/router';
import { useState } from 'react';
import { useQuoteCommonStore } from '@/quote/store/quote/common';
import { updateAction } from '@/quote/util/update-action';
import { QuoteFlowModeEnum } from '@jove-frontend/enums';
import {
  AmericanCountryList,
  BUSINESS_INTERUPTION,
  CMR_COVER,
  COMPUTER_BREAKDOWN_COVER,
  DIRECTORS_OFFICERS_COVER,
  DRIVERS_NEGLIGENCE,
  EMPLOYERS_LIABILITY,
  EMPLOYMENT_PRACTICES_COVER,
  EXCESS_LEVEL,
  EuropeanUnionList,
  FIDELITY_CRIME,
  GOODS_IN_TRANSIT,
  MONEY_ASSAULT_COVER,
  POLICY_EXTEND_COVER_CLAIMS_USA_CANADA,
  PROFESSIONAL_INDEMNITY,
  PROPERTY_COVER,
  PROVIDER_SOURCE,
  PUBLIC_LIABILITY,
  QuoteProceessUrlPath,
  QuoteStatus,
  UKCountryList,
  apiDetermineQuoteSupplierInfo,
  apiGetQuote,
  apiQueryCountryCurrency,
  apiQueryQuoteGenericDocuments,
  apiQuoteGetQuotePrice,
  getDocumentSupportTranslationCountriesMap,
  greateAmericanCoverMap,
} from '@jove-frontend/uikit';
import { apiPatchQuote, useQuoteGlobalCommonInfo } from '@jove-frontend/uikit';
import { useStateMachine } from 'little-state-machine';

type QuotePriceCalculateFuncParamsProp = {
  [key: string]: any;
  needRequestDocuments?: boolean;
  notRequestPriceApi?: boolean;
};

function useQuoteUniversalInterface() {
  const router = useRouter();
  const { state, actions, getState } = useStateMachine({ updateAction });
  const [{ updateQuotePriceLoading }] = useQuoteCommonStore((store) => [store.actions]);
  const { getInsurerInfo } = useQuoteGlobalCommonInfo();
  const [quoteInsurerInfo, setQuoteInsurerInfo] = useState(getInsurerInfo(getState() as any));

  /**
   * request
   */
  const requestQuoteProcessFlowSupplierInfo = async () => {
    const res = await apiDetermineQuoteSupplierInfo();
    return {
      providerCode: PROVIDER_SOURCE().HISCOX_US.value,
    };
  };

  // Universal: query quote price
  const requestQuotePrice = async (extraParams?: QuotePriceCalculateFuncParamsProp) => {
    const quoteId = getState()?.quoteId || router.query.quoteId;
    // If quoteId is null, return
    if (!quoteId) return;

    const params = {
      quoteId,
      ...extraParams,
    } as any;

    // Combine params data
    if (params?.notRequestPriceApi) {
      delete params.notRequestPriceApi;
      params['not_request_price_api'] = true;
    }
    delete params.needRequestDocuments;

    updateQuotePriceLoading(true);
    const [res] = await apiQuoteGetQuotePrice(params, {
      notRequestCoverOptions: getState()?.providerCode === PROVIDER_SOURCE().CHUBB.value,
    });
    updateQuotePriceLoading(false);

    let defaultCoverOptions = {};
    let mergeData = {
      ...getState(),
      billingCycle: res?.billing_cycle,
    };

    let newCovers = {};
    if (res) {
      const {
        pi,
        pl,
        el,
        exl,
        git,
        cmr,
        piOptions = [],
        plOptions = [],
        elOptions = [],
        exlOptions = [],
        gitOptions = [],
        cmrOptions = [],
        fidelityOptions = [],
        currency = 'EUR',
        provider_ref,
        ready_to_pricing,
      } = res || {};

      // If provider_ref is empty and ready_to_pricing is true, it is considered to have no price
      // get-price also have status flag.
      mergeData.cannotCalculatePrice = ready_to_pricing && !provider_ref;

      if (quoteInsurerInfo.isGreateAmerican) {
        // Greate american
        const prePowerUnitOptions = res?.covers_options?.[greateAmericanCoverMap.prePowerUnit.value] || [];
        const deductibleOptions = res?.covers_options?.[greateAmericanCoverMap.deductible.value] || [];
        const reloadingOptions = res?.covers_options?.[greateAmericanCoverMap.reloading.value] || [];
        const debrisRemovalOptions = res?.covers_options?.[greateAmericanCoverMap.debrisRemoval.value] || [];
        const earnedFreightOptions = res?.covers_options?.[greateAmericanCoverMap.earnedFreight.value] || [];
        const fireDepartmentOptions = res?.covers_options?.[greateAmericanCoverMap.fireDepartment.value] || [];
        const lossDataOptions = res?.covers_options?.[greateAmericanCoverMap.lossData.value] || [];
        const rewardCoverageOptions = res?.covers_options?.[greateAmericanCoverMap.rewardCoverage.value] || [];
        const pollutantCleanUpOptions = res?.covers_options?.[greateAmericanCoverMap.pollutantCleanUp.value] || [];
        const nonOwnedTrailerLimitOptions = res?.covers_options?.[greateAmericanCoverMap.nonOwnedTrailerLimit.value] || [];
        const reusablePackingContainersLimitOptions = res?.covers_options?.[greateAmericanCoverMap.reusablePackingContainersLimit.value] || [];
        const tarpsChainsLimitOptions = res?.covers_options?.[greateAmericanCoverMap.tarpsChainsLimit.value] || [];
        defaultCoverOptions = {
          prePowerUnitOptions,
          deductibleOptions,
          reloadingOptions,
          debrisRemovalOptions,
          earnedFreightOptions,
          fireDepartmentOptions,
          lossDataOptions,
          rewardCoverageOptions,
          pollutantCleanUpOptions,
          nonOwnedTrailerLimitOptions,
          reusablePackingContainersLimitOptions,
          tarpsChainsLimitOptions,
        };
        mergeData = {
          ...mergeData,
          price: {
            ...res,
          },
        };

        // Overwrite covers
        if (state.flowMode === QuoteFlowModeEnum.fastTrack) {
          mergeData = {
            ...mergeData,
            greateAmerican: {
              ...mergeData?.greateAmerican,
              covers: {
                ...mergeData?.greateAmerican?.covers,
                ...res.covers,
              },
            },
          };
        }
      } else {
        defaultCoverOptions = {
          piOptions,
          plOptions,
          elOptions,
          exlOptions,
          gitOptions,
          cmrOptions,
          fidelityOptions,
        };
        if (quoteInsurerInfo.isGit) {
          newCovers = {
            [PROFESSIONAL_INDEMNITY]: (piOptions || []).filter((item) => item.selected)?.[0]?.value,
            [PUBLIC_LIABILITY]: (plOptions || []).filter((item) => item.selected)?.[0]?.value,
            [EMPLOYERS_LIABILITY]: (elOptions || []).filter((item) => item.selected)?.[0]?.value,
            [EXCESS_LEVEL]: (exlOptions || []).filter((item) => item.selected)?.[0]?.value,
            [GOODS_IN_TRANSIT]: (gitOptions || []).filter((item) => item.selected)?.[0]?.value,
            [CMR_COVER]: (cmrOptions || []).filter((item) => item.selected)?.[0]?.value,
          };
        } else {
          newCovers = !res.covers || Object.keys(res.covers).length === 0 ? { ...assignTheInitialCoversValue(res) } : { ...res.covers };
        }
        mergeData = {
          ...mergeData,
          price: {
            pi,
            pl,
            el,
            exl,
            cmr,
            git,
            ...res,
            currency,
          },
          covers: newCovers,
        };
      }

      if (quoteInsurerInfo.isRecruitment) {
        // Temporary: Assign default cover value.
        res.covers = newCovers;
      }
    }

    if (res?.quote_status) {
      mergeData.status = res.quote_status;
    }

    actions.updateAction(mergeData);

    if (extraParams?.needRequestDocuments) {
      requestQuoteDocuments();
    }

    return {
      ...res,
      ...defaultCoverOptions,
    };
  };

  const requestQuoteDocuments = async () => {
    const [res] = await apiQueryQuoteGenericDocuments({
      quoteId: state.quoteId,
      language: (state.language || getDocumentSupportTranslationCountriesMap().GB.code)?.toLowerCase(),
    });
    if (res) {
      actions.updateAction({
        ...getState(),
        documents: res?.data || [],
      });
    }
  };

  /**
   * Update meta info
   */
  const requestOnlyPatchMetaInfo = (data: { routeKey?: string }) => {
    if (data.routeKey && state.quoteId) {
      let path = QuoteProceessUrlPath[data?.routeKey];
      if (path) {
        path = path.replace('${quoteId}', state.quoteId);
      }
      const params = {
        meta: {
          ...state?.meta,
          route: path,
          path,
          route_key: data.routeKey,
        },
      };
      apiPatchQuote(state.quoteId, params as any);
    }
  };

  /**
   * Get current currency
   */
  const requestCurrentCurrency = async (country?: string) => {
    const currentCountry = country || state?.contact?.country || state?.business?.businessCountry || 'GB';
    let code = 'GBP';
    let symbol = '£';

    if (UKCountryList.includes(currentCountry)) {
      code = 'GBP';
      symbol = '£';
    } else if (AmericanCountryList.includes(currentCountry)) {
      code = 'USD';
      symbol = '$';
    } else if (EuropeanUnionList.includes(currentCountry)) {
      code = 'EUR';
      symbol = '€';
    } else {
      const [res] = await apiQueryCountryCurrency({ country: currentCountry });

      if (res) {
        code = res?.currencies?.[0]?.code || 'GBP';
        symbol = res?.currencies?.[0]?.symbol_native || '£';
      }
    }

    actions.updateAction({
      ...getState(),
      currency: {
        code,
        symbol,
      },
      country,
    });
  };

  /**
   * Get quote detail
   */
  const requestGetQuoteDetailInfo = async (quoteId) => {
    const [res] = await apiGetQuote(quoteId);
    return res;
  };

  // Assign default covers
  const assignTheInitialCoversValue = (res) => {
    const coverOptions = res.covers_options;
    const covers: any = {};
    Object.keys(coverOptions || {}).forEach((key) => {
      const defaultOption = coverOptions?.[key]?.find((item) => !!item.is_default);
      if (defaultOption) {
        covers[key] = defaultOption.value;
      }
    });

    return covers;
  };

  return {
    requestQuoteProcessFlowSupplierInfo,
    requestQuotePrice,
    requestCurrentCurrency,
    requestGetQuoteDetailInfo,
    requestOnlyPatchMetaInfo,
  };
}

export default useQuoteUniversalInterface;
